import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { getAdminGetNotificationTemplatesQueryKey, useAdminCreateNotificationTemplate } from '@/api/auth/admin/admin';
import { CreateNotificationTemplateRequestDefaultMode as DefaultNotificationMode } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Dialog for adding a new notification template
 */
export function AddNotificationTemplateDialog() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [eventId, setEventId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [defaultMode, setDefaultMode] = useState<DefaultNotificationMode>(DefaultNotificationMode.Enabled);

  const { mutate: createTemplate, isPending: isCreating } = useAdminCreateNotificationTemplate({
    mutation: {
      onSuccess: () => {
        toast.success('Notification template created');
        setIsOpen(false);
        resetForm();
        queryClient.invalidateQueries({
          queryKey: getAdminGetNotificationTemplatesQueryKey(),
        });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'CreateNotificationTemplate',
          eventId: eventId.trim(),
        });
        toast.error('Failed to create template. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const resetForm = () => {
    setEventId('');
    setIsActive(true);
    setDefaultMode(DefaultNotificationMode.Enabled);
  };

  const handleCreate = () => {
    createTemplate({
      data: {
        eventName: eventId.trim(),
        isActive,
        defaultMode,
      },
    });
  };

  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const isValid = eventId.trim();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Template
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Notification Template</DialogTitle>
          <DialogDescription>
            Create a new notification template for an event type
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="eventId">Event ID</Label>
            <Input
              id="eventId"
              type="text"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              placeholder="UserInvited"
              maxLength={100}
            />
            <p className="text-sm text-muted-foreground">
              The event type this template is for (e.g., UserInvited, PaymentFailed)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="isActive" checked={isActive} onCheckedChange={setIsActive} />
            <Label htmlFor="isActive">Active</Label>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="defaultMode">Default Mode</Label>
            <Select value={defaultMode} onValueChange={(value) => setDefaultMode(value as DefaultNotificationMode)}>
              <SelectTrigger id="defaultMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DefaultNotificationMode.Enabled}>Enabled</SelectItem>
                <SelectItem value={DefaultNotificationMode.Disabled}>Disabled</SelectItem>
                <SelectItem value={DefaultNotificationMode.Critical}>Critical</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {defaultMode === DefaultNotificationMode.Enabled && 'Users receive this notification unless they opt out'}
              {defaultMode === DefaultNotificationMode.Disabled && 'Users must opt in to receive this notification'}
              {defaultMode === DefaultNotificationMode.Critical && 'Users always receive this notification (cannot opt out)'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !isValid}>
            {isCreating ? 'Creating...' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
